// TenantFocusForm — chip-input form for tenants.settings.focus.
//
// Single component, two surfaces: the standalone Settings page
// (_authed.settings.focus.tsx) and the first-login modal
// (FirstLoginFocusModal.tsx). The form is fully controlled — parent owns
// state, this component just renders inputs + emits onChange.
//
// Validation matches the server-side TenantFocus Zod schema:
//   * naics:          2-6 digit codes
//   * psc:            <= 8 chars
//   * categories:     tag slugs from categories.csv (e.g. "it-software")
//   * geography:      ISO state code (2 uppercase letters)
//   * certifications: free-form, capped at 32 chars
//   * agencies:       free-form, capped at 64 chars
// We don't strictly enforce here — the server will reject anything off-spec
// and the UI surfaces the error. Keeping the form lenient avoids fighting
// the user mid-typing.

import { useState } from 'react';

export interface TenantFocus {
  naics?: string[];
  psc?: string[];
  categories?: string[];
  certifications?: string[];
  geography?: string[];
  agencies?: string[];
}

export interface TenantFocusFormProps {
  value: TenantFocus;
  onChange: (next: TenantFocus) => void;
}

// ─── Category taxonomy (categories.csv inlined) ───────────────────────────
// Tag slugs are immutable IDs. Labels may change; slugs must not.
interface CategoryTag {
  key: string;
  label: string;
}
interface CategoryGroup {
  group: string;
  tags: CategoryTag[];
}

const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    group: 'Construction & Engineering',
    tags: [
      { key: 'construction', label: 'Construction' },
      { key: 'engineering-architecture', label: 'Engineering & Architecture' },
    ],
  },
  {
    group: 'Technology & Telecom',
    tags: [
      { key: 'it-software', label: 'IT & Software' },
      { key: 'telecommunications', label: 'Telecommunications' },
    ],
  },
  {
    group: 'Professional & Business Services',
    tags: [
      { key: 'professional-services', label: 'Professional Services' },
      { key: 'business-administration', label: 'Business & Admin Services' },
      { key: 'financial-services', label: 'Financial Services' },
      { key: 'legal-services', label: 'Legal Services' },
      { key: 'research-development', label: 'Research & Development' },
    ],
  },
  {
    group: 'Healthcare & Life Sciences',
    tags: [{ key: 'healthcare-medical', label: 'Healthcare & Medical' }],
  },
  {
    group: 'Manufacturing & Industrial',
    tags: [
      { key: 'manufacturing-industrial', label: 'Manufacturing & Industrial' },
      { key: 'automotive-vehicles', label: 'Automotive & Vehicles' },
      { key: 'aerospace', label: 'Aerospace' },
      { key: 'marine-maritime', label: 'Marine & Maritime' },
    ],
  },
  {
    group: 'Defense & Security',
    tags: [{ key: 'defense-security', label: 'Defense & Security' }],
  },
  {
    group: 'Energy & Environment',
    tags: [
      { key: 'energy-utilities', label: 'Energy & Utilities' },
      { key: 'environmental-services', label: 'Environmental Services' },
    ],
  },
  {
    group: 'Transport & Logistics',
    tags: [
      { key: 'transportation-services', label: 'Transportation Services' },
      { key: 'logistics-warehousing', label: 'Logistics & Warehousing' },
    ],
  },
  {
    group: 'Agriculture & Food',
    tags: [
      { key: 'agriculture-forestry', label: 'Agriculture & Forestry' },
      { key: 'food-beverage', label: 'Food & Beverage' },
    ],
  },
  {
    group: 'Facilities & Cleaning',
    tags: [
      { key: 'facilities-maintenance', label: 'Facilities & Maintenance' },
      { key: 'cleaning-janitorial', label: 'Cleaning & Janitorial' },
    ],
  },
  {
    group: 'Education & Training',
    tags: [{ key: 'education-training', label: 'Education & Training' }],
  },
  {
    group: 'Consumer, Hospitality & Community',
    tags: [
      { key: 'hospitality-travel', label: 'Hospitality & Travel' },
      { key: 'arts-recreation', label: 'Arts & Recreation' },
      { key: 'personal-services', label: 'Personal Services' },
      { key: 'public-community', label: 'Public & Community' },
    ],
  },
];

// ─── Free-text chip field definition ──────────────────────────────────────
interface FieldDef {
  key: Exclude<keyof TenantFocus, 'categories'>;
  label: string;
  hint: string;
  presets?: string[];
}

const FIELDS: FieldDef[] = [
  {
    key: 'naics',
    label: 'NAICS',
    hint: 'Industry codes you target. Type a code and press Enter. Common federal IT codes pre-loaded.',
    presets: ['541512', '541511', '541330', '541715', '541519'],
  },
  {
    key: 'psc',
    label: 'PSC',
    hint: 'Product/Service Codes. D-series for IT services, R-series for R&D.',
    presets: ['D307', 'D399', 'R425', 'R408'],
  },
  {
    key: 'certifications',
    label: 'SET-ASIDE / CERTS',
    hint: "Your firm's certifications. Used to filter set-aside opportunities.",
    presets: ['8(a)', 'WOSB', 'EDWOSB', 'HUBZone', 'SDVOSB', 'VOSB'],
  },
  {
    key: 'geography',
    label: 'STATES',
    hint: 'Two-letter state codes (place of performance).',
    presets: ['VA', 'MD', 'DC', 'TX', 'CA'],
  },
  {
    key: 'agencies',
    label: 'AGENCIES',
    hint: 'Target buying agencies. The AI rail will lean toward opportunities from these.',
    presets: ['DOD', 'DHS', 'VA', 'HHS', 'DOE', 'NASA', 'GSA'],
  },
];

export function TenantFocusForm({ value, onChange }: TenantFocusFormProps) {
  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <CategoryPicker
        value={value.categories ?? []}
        onChange={(cats) => onChange({ ...value, categories: cats })}
      />
      {FIELDS.map((field) => (
        <FocusField
          key={field.key}
          field={field}
          values={value[field.key] ?? []}
          onValuesChange={(next) => onChange({ ...value, [field.key]: next })}
        />
      ))}
    </div>
  );
}

// ─── Category picker ───────────────────────────────────────────────────────

function CategoryPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const selected = new Set(value);

  function toggle(key: string) {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next]);
  }

  return (
    <fieldset style={{ border: '1px solid var(--bd)', padding: '10px 12px 12px', borderRadius: 0 }}>
      <legend
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
        }}
      >
        CATEGORIES
      </legend>
      <div className="meta mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 10 }}>
        Industry categories you target. The AI rail seeds from these when NAICS codes aren&apos;t
        set.
        {value.length > 0 && (
          <span style={{ color: 'var(--primary)', marginLeft: 8 }}>{value.length} selected</span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {CATEGORY_GROUPS.map(({ group, tags }) => (
          <div key={group}>
            <div
              className="meta mono"
              style={{
                fontSize: 10,
                letterSpacing: 0.4,
                color: 'var(--fg-3)',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              {group}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {tags.map((tag) => (
                <button
                  key={tag.key}
                  type="button"
                  onClick={() => toggle(tag.key)}
                  className={selected.has(tag.key) ? 'btn xs pri' : 'btn xs gh'}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </fieldset>
  );
}

// ─── Free-text chip field ──────────────────────────────────────────────────

function FocusField({
  field,
  values,
  onValuesChange,
}: {
  field: FieldDef;
  values: string[];
  onValuesChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const set = new Set(values);

  function add(raw: string) {
    const v = raw.trim();
    if (!v || set.has(v)) return;
    onValuesChange([...values, v]);
  }
  function remove(v: string) {
    onValuesChange(values.filter((x) => x !== v));
  }

  return (
    <fieldset style={{ border: '1px solid var(--bd)', padding: '10px 12px 12px', borderRadius: 0 }}>
      <legend
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
        }}
      >
        {field.label}
      </legend>
      <div className="meta mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 8 }}>
        {field.hint}
      </div>
      {/* selected chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {values.map((v) => (
          <span
            key={v}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              height: 22,
              padding: '0 4px 0 8px',
              background: 'var(--primary-bg-faint, rgba(52,120,246,0.08))',
              border: '1px solid var(--primary-line, rgba(52,120,246,0.45))',
              color: 'var(--fg-1)',
              fontFamily: 'var(--mono)',
              fontSize: 11,
            }}
          >
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              aria-label={`remove ${v}`}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--fg-3)',
                fontSize: 14,
                lineHeight: '14px',
              }}
            >
              ×
            </button>
          </span>
        ))}
        {values.length === 0 && (
          <span className="meta mono" style={{ color: 'var(--fg-3)' }}>
            (none)
          </span>
        )}
      </div>

      {/* presets — one-click quick add */}
      {field.presets && field.presets.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          <span className="meta mono" style={{ color: 'var(--fg-3)', marginRight: 4 }}>
            quick add:
          </span>
          {field.presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => (set.has(p) ? remove(p) : add(p))}
              className="btn xs gh"
              style={{
                opacity: set.has(p) ? 0.5 : 1,
                textDecoration: set.has(p) ? 'line-through' : 'none',
              }}
              title={set.has(p) ? 'click to remove' : 'click to add'}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* free-text entry */}
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(draft);
              setDraft('');
            }
          }}
          placeholder={`add ${field.label.toLowerCase()}…`}
          spellCheck={false}
          style={{
            flex: 1,
            height: 24,
            padding: '0 6px',
            background: 'var(--bg-base)',
            border: '1px solid var(--bd-strong)',
            color: 'var(--fg-1)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
          }}
        />
        <button
          type="button"
          onClick={() => {
            add(draft);
            setDraft('');
          }}
          className="btn xs gh"
          disabled={!draft.trim()}
        >
          ADD
        </button>
      </div>
    </fieldset>
  );
}
